/**
 * Modal Context
 * Centralized modal management system for confirmations, alerts, and custom content
 */

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

export interface ModalAction {
  label: string;
  onClick: () => void;
  variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
  loading?: boolean;
}

export interface ConfirmationModalData {
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmVariant?: 'default' | 'destructive';
  onConfirm: () => void | Promise<void>;
  onCancel?: () => void;
}

export interface AlertModalData {
  title: string;
  description: string;
  actionLabel?: string;
  onAction?: () => void;
}

export interface CustomModalData {
  title?: string;
  description?: string;
  content: ReactNode;
  actions?: ModalAction[];
  size?: 'sm' | 'md' | 'lg' | 'xl';
  dismissible?: boolean;
}

type ModalType = 'confirmation' | 'alert' | 'custom';

interface ModalState {
  isOpen: boolean;
  type: ModalType | null;
  data: ConfirmationModalData | AlertModalData | CustomModalData | null;
  loading: boolean;
}

interface ModalContextType {
  // State
  isOpen: boolean;
  loading: boolean;
  
  // Confirmation modals
  showConfirmation: (data: ConfirmationModalData) => void;
  
  // Alert modals  
  showAlert: (data: AlertModalData) => void;
  
  // Custom modals
  showModal: (data: CustomModalData) => void;
  
  // Control
  closeModal: () => void;
  setLoading: (loading: boolean) => void;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export function useModal() {
  const context = useContext(ModalContext);
  if (context === undefined) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
}

interface ModalProviderProps {
  children: ReactNode;
}

export function ModalProvider({ children }: ModalProviderProps) {
  const [modalState, setModalState] = useState<ModalState>({
    isOpen: false,
    type: null,
    data: null,
    loading: false,
  });

  const showConfirmation = useCallback((data: ConfirmationModalData) => {
    setModalState({
      isOpen: true,
      type: 'confirmation',
      data,
      loading: false,
    });
  }, []);

  const showAlert = useCallback((data: AlertModalData) => {
    setModalState({
      isOpen: true,
      type: 'alert',
      data,
      loading: false,
    });
  }, []);

  const showModal = useCallback((data: CustomModalData) => {
    setModalState({
      isOpen: true,
      type: 'custom',
      data,
      loading: false,
    });
  }, []);

  const closeModal = useCallback(() => {
    setModalState(prev => ({
      ...prev,
      isOpen: false,
      loading: false,
    }));
    
    // Clear state after animation
    setTimeout(() => {
      setModalState({
        isOpen: false,
        type: null,
        data: null,
        loading: false,
      });
    }, 200);
  }, []);

  const setLoading = useCallback((loading: boolean) => {
    setModalState(prev => ({
      ...prev,
      loading,
    }));
  }, []);

  const handleConfirmation = async () => {
    if (modalState.type !== 'confirmation' || !modalState.data) return;
    
    const data = modalState.data as ConfirmationModalData;
    setLoading(true);
    
    try {
      await data.onConfirm();
      closeModal();
    } catch (error) {
      console.error('Confirmation action failed:', error);
      setLoading(false);
    }
  };

  const handleAlert = () => {
    if (modalState.type !== 'alert' || !modalState.data) return;
    
    const data = modalState.data as AlertModalData;
    data.onAction?.();
    closeModal();
  };

  const handleCancel = () => {
    if (modalState.type === 'confirmation' && modalState.data) {
      const data = modalState.data as ConfirmationModalData;
      data.onCancel?.();
    }
    closeModal();
  };

  const contextValue: ModalContextType = {
    isOpen: modalState.isOpen,
    loading: modalState.loading,
    showConfirmation,
    showAlert,
    showModal,
    closeModal,
    setLoading,
  };

  return (
    <ModalContext.Provider value={contextValue}>
      {children}
      
      {/* Confirmation Modal */}
      {modalState.type === 'confirmation' && modalState.data && (
        <AlertDialog open={modalState.isOpen} onOpenChange={(open) => !open && handleCancel()}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {(modalState.data as ConfirmationModalData).title}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {(modalState.data as ConfirmationModalData).description}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel 
                onClick={handleCancel}
                disabled={modalState.loading}
              >
                {(modalState.data as ConfirmationModalData).cancelLabel || 'Cancel'}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmation}
                disabled={modalState.loading}
                className={
                  (modalState.data as ConfirmationModalData).confirmVariant === 'destructive' 
                    ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' 
                    : ''
                }
              >
                {modalState.loading ? 'Processing...' : 
                 (modalState.data as ConfirmationModalData).confirmLabel || 'Confirm'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      {/* Alert Modal */}
      {modalState.type === 'alert' && modalState.data && (
        <AlertDialog open={modalState.isOpen} onOpenChange={(open) => !open && closeModal()}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {(modalState.data as AlertModalData).title}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {(modalState.data as AlertModalData).description}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogAction onClick={handleAlert}>
                {(modalState.data as AlertModalData).actionLabel || 'OK'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      {/* Custom Modal */}
      {modalState.type === 'custom' && modalState.data && (
        <Dialog 
          open={modalState.isOpen} 
          onOpenChange={(open) => {
            const customData = modalState.data as CustomModalData;
            if (!open && (customData.dismissible !== false)) {
              closeModal();
            }
          }}
        >
          <DialogContent 
            className={
              (modalState.data as CustomModalData).size === 'sm' ? 'max-w-sm' :
              (modalState.data as CustomModalData).size === 'lg' ? 'max-w-2xl' :
              (modalState.data as CustomModalData).size === 'xl' ? 'max-w-4xl' :
              'max-w-lg'
            }
          >
            {((modalState.data as CustomModalData).title || (modalState.data as CustomModalData).description) && (
              <DialogHeader>
                {(modalState.data as CustomModalData).title && (
                  <DialogTitle>{(modalState.data as CustomModalData).title}</DialogTitle>
                )}
                {(modalState.data as CustomModalData).description && (
                  <DialogDescription>{(modalState.data as CustomModalData).description}</DialogDescription>
                )}
              </DialogHeader>
            )}
            
            <div className="py-4">
              {(modalState.data as CustomModalData).content}
            </div>
            
            {(modalState.data as CustomModalData).actions && (
              <DialogFooter>
                {(modalState.data as CustomModalData).actions!.map((action, index) => (
                  <Button
                    key={index}
                    variant={action.variant || 'default'}
                    onClick={action.onClick}
                    disabled={action.loading || modalState.loading}
                  >
                    {action.loading ? 'Loading...' : action.label}
                  </Button>
                ))}
              </DialogFooter>
            )}
          </DialogContent>
        </Dialog>
      )}
    </ModalContext.Provider>
  );
}

// Convenience hooks for common modal patterns
export function useConfirmationModal() {
  const { showConfirmation } = useModal();
  
  return useCallback((
    title: string,
    description: string,
    onConfirm: () => void | Promise<void>,
    options?: Partial<ConfirmationModalData>
  ) => {
    showConfirmation({
      title,
      description,
      onConfirm,
      ...options,
    });
  }, [showConfirmation]);
}

export function useAlertModal() {
  const { showAlert } = useModal();
  
  return useCallback((
    title: string,
    description: string,
    options?: Partial<AlertModalData>
  ) => {
    showAlert({
      title,
      description,
      ...options,
    });
  }, [showAlert]);
}

export function useCustomModal() {
  const { showModal } = useModal();
  
  return useCallback((
    content: ReactNode,
    options?: Partial<Omit<CustomModalData, 'content'>>
  ) => {
    showModal({
      content,
      ...options,
    });
  }, [showModal]);
}